import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/page-header";
import { DateRangeFilter } from "@/components/date-range-filter";
import { BarList, Panel, StatTile, HeroTile } from "@/components/analytics-ui";
import { MetricToggleChart, type ToggleMetric } from "@/components/metric-chart";
import { AreaChart } from "@/components/area-chart";
import { SkAnalyticsPage } from "@/components/skeleton";
import { PageQuery } from "@/components/page-query";
import { enumerateDays } from "@shared/date-range";
import { fmtDateTime } from "@shared/format";
import type { AgencyMetaDaily } from "@shared/agency-types";
import type { MetaPage } from "@shared/pages/meta";
import { pageQuery } from "@/lib/queries";
import { useTitle } from "@/lib/title";

// Meta, split per surface so each one reads at a glance:
// Ads (spend + conversions), Facebook (followers/views/posts), Instagram
// (followers/reach/posts), Messages (Page inbox). All served by the Digital
// King hub feed; sections the hub doesn't return simply have no tab.

export const Route = createFileRoute("/_app/meta/")({
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(pageQuery<MetaPage>("meta", deps));
  },
  component: MetaRoute,
});

const SECTIONS: Record<string, { title: string; info?: string }> = {
  ads: { title: "Meta ads" },
  facebook: { title: "Facebook page" },
  instagram: { title: "Instagram" },
  messages: { title: "Messages", info: "Messenger and Instagram inbox." },
};

const usd = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: n >= 100 ? 0 : 2 });
const num = (n: number) => n.toLocaleString("en-US");
const pct = (n: number) => `${(n * 100).toFixed(2)}%`;

const th = "py-1.5 pr-3 font-medium";
const td = "py-2 pr-3 tabular-nums";

const PLATFORM_LABELS: Record<string, string> = {
  facebook: "Facebook",
  instagram: "Instagram",
  audience_network: "Audience Network",
  messenger: "Messenger",
  unknown: "Other",
};

const FB_ICON = (
  <svg className="size-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.09 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.7 4.53-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.09 24 18.1 24 12.07z" />
  </svg>
);
const IG_ICON = (
  <svg className="size-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <rect x="2" y="2" width="20" height="20" rx="5" /><circle cx="12" cy="12" r="4.5" /><circle cx="17.3" cy="6.7" r="1.3" fill="currentColor" stroke="none" />
  </svg>
);

function MetaRoute() {
  useTitle("Meta");
  const search = Route.useSearch();
  return (
    <PageQuery<MetaPage> name="meta" search={search} skeleton={<SkAnalyticsPage />}>
      {(page) => <Meta page={page} />}
    </PageQuery>
  );
}

function Meta({ page }: { page: MetaPage }) {
  const { configured, report: live, presets, range, rangeLabel } = page;

  if (!configured || !live) {
    return (
      <div className="page-shell">
        <PageHeader title="Meta" />
        <Panel title={!configured ? "Not connected" : "No data"} className="mt-5">
          <p className="text-sm text-muted">
            {!configured
              ? "Meta numbers show here once Digital King connects the Meta account."
              : "Meta numbers aren't available for this account right now."}
          </p>
        </Panel>
      </div>
    );
  }
  const report = live;
  const tab = page.tab ?? "ads";

  const days = enumerateDays(range);

  /* -- Ads tab data -- */
  const ads = report.ads;
  let metrics: ToggleMetric[] = [];
  let cpc: number | null = null;
  if (ads) {
    const byDate = new Map(ads.daily.map((d) => [d.date, d]));
    const daily = (f: (d: AgencyMetaDaily) => number) =>
      days.map(({ date }) => {
        const d = byDate.get(date);
        return d ? f(d) : 0;
      });
    const t = ads.totals;
    cpc = t.spend !== undefined && t.clicks > 0 ? t.spend / t.clicks : null;
    const costPerConv =
      t.spend !== undefined && t.conversions !== undefined && t.conversions > 0
        ? t.spend / t.conversions
        : null;
    metrics = [
      ...(t.spend !== undefined
        ? [{
            key: "spend", label: "Spend", color: "#662d91", axis: "hidden", format: "usd",
            value: usd(t.spend), points: daily((d) => d.spend ?? 0), defaultOn: true,
          } satisfies ToggleMetric]
        : []),
      {
        key: "clicks", label: "Clicks", color: "#4b7308", axis: "left", format: "number",
        value: num(t.clicks), points: daily((d) => d.clicks), defaultOn: true,
      },
      {
        key: "impressions", label: "Impressions", color: "#4d94e8", axis: "right", format: "number",
        value: num(t.impressions), points: daily((d) => d.impressions),
      },
      {
        key: "reach", label: "Reach", color: "#00897B", axis: "hidden", format: "number",
        value: num(t.reach), points: daily((d) => d.reach),
      },
      ...(t.conversions !== undefined
        ? [{
            key: "conversions", label: "Conversions", color: "#E8710A", axis: "hidden", format: "number",
            value: num(Math.round(t.conversions)), points: daily((d) => d.conversions ?? 0),
          } satisfies ToggleMetric]
        : []),
      ...(costPerConv !== null
        ? [{
            key: "costPerConv", label: "Cost / conv.", color: "#C2185B", axis: "hidden", format: "usd",
            value: usd(costPerConv),
            points: daily((d) => ((d.conversions ?? 0) > 0 ? (d.spend ?? 0) / d.conversions! : 0)),
          } satisfies ToggleMetric]
        : []),
    ];
  }
  const showSpend = ads?.platforms.some((p) => p.spend !== undefined) ?? false;

  /* -- Facebook tab data -- */
  // `?? 0` everywhere: an older hub deploy may serve a previous daily shape.
  const fbPage = report.page;
  const fbNewFollows = fbPage?.daily.reduce((s, d) => s + (d.newFollows ?? 0), 0) ?? 0;
  const fbViews = fbPage?.daily.reduce((s, d) => s + (d.views ?? 0), 0) ?? 0;
  const fbEngagements = fbPage?.daily.reduce((s, d) => s + (d.engagements ?? 0), 0) ?? 0;

  /* -- Instagram tab data -- */
  const ig = report.instagram;
  const igNewFollowers = ig?.daily.reduce((s, d) => s + (d.newFollowers ?? 0), 0) ?? 0;

  const seriesFor = <T,>(rows: T[] | undefined, date: (r: T) => string, value: (r: T) => number) => {
    const m = new Map((rows ?? []).map((r) => [date(r), value(r) || 0]));
    return days.map(({ date: d }) => m.get(d) ?? 0);
  };

  return (
    <div className="page-shell">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader title={SECTIONS[tab]?.title ?? "Meta"} info={SECTIONS[tab]?.info} />
        <DateRangeFilter presets={presets} defaultKey="30d" />
      </div>


      {tab === "ads" && ads && (
        <>
          <div className="mt-5">
            <MetricToggleChart labels={days.map((d) => d.label)} metrics={metrics} />
          </div>
          <p className="mt-2 text-xs text-muted">
            CTR {ads.totals.ctr !== undefined ? pct(ads.totals.ctr) : "–"}
            {cpc !== null ? ` · average cost per click ${usd(cpc)}` : ""}
          </p>

          {ads.campaigns && ads.campaigns.length > 0 && (
            <Panel title="Campaigns" className="mt-5">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted">
                      <th className={th}>Campaign</th>
                      {showSpend && <th className={`${th} text-right`}>Spend</th>}
                      <th className={`${th} text-right`}>Impr.</th>
                      <th className={`${th} text-right`}>Clicks</th>
                      <th className={`${th} text-right`}>CTR</th>
                      {ads.campaigns.some((c) => c.conversions !== undefined) && (
                        <th className={`${th} text-right`}>Conversions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {ads.campaigns.map((c) => (
                      <tr key={c.name} className="border-t border-border">
                        <td className="py-2 pr-3 text-foreground">
                          {c.name}
                          {c.status !== "active" && <span className="text-muted"> ({c.status})</span>}
                        </td>
                        {showSpend && (
                          <td className={`${td} text-right`}>{c.spend !== undefined ? usd(c.spend) : "-"}</td>
                        )}
                        <td className={`${td} text-right`}>{num(c.impressions)}</td>
                        <td className={`${td} text-right`}>{num(c.clicks)}</td>
                        <td className={`${td} text-right`}>{c.impressions > 0 ? pct(c.clicks / c.impressions) : "-"}</td>
                        {c.conversions !== undefined && (
                          <td className={`${td} text-right`}>{num(Math.round(c.conversions))}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}

          <div className="mt-4 grid gap-4 @3xl:grid-cols-2">
            <Panel title={`By platform (${showSpend ? "$ spend" : "clicks"})`}>
              <BarList
                toneClass="bg-primary"
                items={ads.platforms.map((p) => ({
                  label: PLATFORM_LABELS[p.label] ?? p.label,
                  count: p.spend !== undefined ? Math.round(p.spend) : p.clicks,
                  sub: `${num(p.clicks)} clicks · ${num(p.impressions)} impr.`,
                }))}
                emptyText="No platform data in this range."
              />
            </Panel>
            <Panel title={`By age group (${showSpend ? "$ spend" : "clicks"})`}>
              <BarList
                items={ads.ages.map((a) => ({
                  label: a.label,
                  count: a.spend !== undefined ? Math.round(a.spend) : a.clicks,
                  sub: `${num(a.clicks)} clicks`,
                }))}
                emptyText="No demographic data in this range."
              />
            </Panel>
          </div>
        </>
      )}

      {tab === "facebook" && fbPage && (
        <>
          <div className="mt-5 grid grid-cols-2 gap-3 @3xl:grid-cols-4">
            <div className="col-span-2 @3xl:col-span-1 [&>div]:h-full">
              <HeroTile label="Page followers" value={num(fbPage.followers)} sub={fbPage.name} icon={FB_ICON} />
            </div>
            <StatTile label="New followers" value={`+${num(fbNewFollows)}`} sub={rangeLabel} />
            <StatTile label="Page views" value={num(fbViews)} sub={rangeLabel} />
            <StatTile label="Post engagements" value={num(fbEngagements)} sub="likes, comments, shares…" />
          </div>

          {fbPage.daily.length > 0 && (
            <Panel title="Growth & engagement · daily" className="mt-4">
              <AreaChart
                labels={days.map((d) => d.label)}
                series={[
                  { name: "New followers", color: "var(--primary)", points: seriesFor(fbPage.daily, (d) => d.date, (d) => d.newFollows) },
                  { name: "Post engagements", color: "var(--accent)", points: seriesFor(fbPage.daily, (d) => d.date, (d) => d.engagements), axis: "right" },
                ]}
              />
            </Panel>
          )}

          <Panel title="Recent posts" className="mt-4">
            <ul className="grid gap-3 @3xl:grid-cols-2">
              {fbPage.posts.map((p) => (
                <li key={p.id} className="rounded-lg border border-border p-3 text-sm">
                  <p className="line-clamp-2 text-foreground">
                    {p.permalink ? (
                      <a href={p.permalink} target="_blank" rel="noreferrer" className="hover:text-accent-dark">
                        {p.message || "(photo / video post)"}
                      </a>
                    ) : (
                      p.message || "(photo / video post)"
                    )}
                  </p>
                  <p className="mt-1.5 text-xs text-muted">
                    {fmtDateTime(p.createdAt)} · 👍 {num(p.likes)} · 💬 {num(p.comments)} · ↗ {num(p.shares)}
                  </p>
                </li>
              ))}
              {fbPage.posts.length === 0 && (
                <li className="py-2 text-center text-xs text-muted">No recent posts.</li>
              )}
            </ul>
          </Panel>
        </>
      )}

      {tab === "instagram" && ig && (
        <>
          <div className="mt-5 grid grid-cols-2 gap-3 @3xl:grid-cols-4">
            <div className="col-span-2 @3xl:col-span-1 [&>div]:h-full">
              <HeroTile label="Instagram followers" value={num(ig.followers)} sub={`@${ig.username}`} icon={IG_ICON} />
            </div>
            <StatTile label="New followers" value={`+${num(igNewFollowers)}`} sub={rangeLabel} />
            <StatTile
              label="Profile views"
              value={ig.totals.profileViews !== null ? num(ig.totals.profileViews) : "–"}
              sub={rangeLabel}
            />
            <StatTile
              label="Content views"
              value={ig.totals.contentViews !== null ? num(ig.totals.contentViews) : "–"}
              sub={ig.totals.accountsEngaged !== null ? `${num(ig.totals.accountsEngaged)} accounts engaged` : rangeLabel}
            />
          </div>

          {ig.daily.length > 0 && (
            <Panel title="Reach & growth · daily" className="mt-4">
              <AreaChart
                labels={days.map((d) => d.label)}
                series={[
                  { name: "Reach", color: "var(--primary)", points: seriesFor(ig.daily, (d) => d.date, (d) => d.reach) },
                  { name: "New followers", color: "var(--accent)", points: seriesFor(ig.daily, (d) => d.date, (d) => d.newFollowers), axis: "right" },
                ]}
              />
            </Panel>
          )}

          <Panel title="Recent posts" className="mt-4">
            <ul className="grid gap-3 @3xl:grid-cols-2">
              {ig.media.map((m) => (
                <li key={m.id} className="rounded-lg border border-border p-3 text-sm">
                  <p className="line-clamp-2 text-foreground">
                    {m.permalink ? (
                      <a href={m.permalink} target="_blank" rel="noreferrer" className="hover:text-accent-dark">
                        {m.caption || "(no caption)"}
                      </a>
                    ) : (
                      m.caption || "(no caption)"
                    )}
                  </p>
                  <p className="mt-1.5 text-xs text-muted">
                    {fmtDateTime(m.timestamp)} · {m.mediaType.replace(/_/g, " ")} · ❤️ {num(m.likes)} · 💬 {num(m.comments)}
                  </p>
                </li>
              ))}
              {ig.media.length === 0 && (
                <li className="py-2 text-center text-xs text-muted">No recent posts.</li>
              )}
            </ul>
          </Panel>
        </>
      )}

      {tab === "messages" && report.messages && (
        <Panel
          title="Messenger inbox"
          right={
            <span className="text-xs text-muted">
              {report.messages.conversations.filter((c) => c.unreadCount > 0).length} unread
            </span>
          }
          className="mt-5"
        >
          <ul>
            {report.messages.conversations.map((c, i) => (
              <li
                key={c.id}
                className={`flex flex-wrap items-center gap-3 py-3 ${i > 0 ? "border-t border-border" : ""}`}
              >
                <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-surface-2 font-heading text-sm font-bold text-foreground">
                  {c.participant.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 text-sm font-medium text-foreground">
                    {c.participant}
                    {c.unreadCount > 0 && (
                      <span className="inline-flex min-w-[1.1rem] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                        {c.unreadCount}
                      </span>
                    )}
                  </p>
                  <p className="truncate text-xs text-muted">{c.snippet}</p>
                </div>
                <span className="text-xs tabular-nums text-muted">{fmtDateTime(c.updatedAt)}</span>
              </li>
            ))}
            {report.messages.conversations.length === 0 && (
              <li className="py-6 text-center text-sm text-muted">Inbox is empty.</li>
            )}
          </ul>
          <p className="mt-3 text-xs text-muted">
            Read-only view. Replying happens in Meta Business Suite.
          </p>
        </Panel>
      )}
    </div>
  );
}
